import React, { useEffect, useState, useContext, useRef, useLayoutEffect } from 'react'
import styles from './projectInsight.module.css';
import AkiraPictogram from "../../assets/akira_pictogram.svg";
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { botName, BotResponse, createNewConversationNew, getListAvailableAgents, historyRead } from '../../api';
import { AvailableAgentsList, Citation, CosmosDBStatus, UserInfo, UserMessage, BotConversation } from '../../api/models'
import * as AdaptiveCards from 'adaptivecards';
import 'adaptivecards/dist/adaptivecards.css';
import AdaptiveCardRenderer from '../../components/AdaptiveCards/AdaptiveCard';
import { AppStateContext } from '../../state/AppProvider';
import { useMsal } from '@azure/msal-react';
import { agents, project_insight_options } from './constant';
import { createUserInfoList, decodeToken, getCurrentTimestamp, getDropdownLabel, getTeamsAuthToken, handleChatHistoryDeleted, isJson, onViewSource, parseCitationFromMessage, parseCitations, setAccessTokenInLocalStorage, setEmailInLocalStorage, setUserIdInLocalStorage } from '../bot/common';
import { ApiPostFunctionRequest, AvailableParameters, ChoicesChoiceSet, fetchInputs, KeyValuePair, PortfolioInsightsPayload } from '../bot/interfaces';
import { Answer } from '../../components/Answer';
import { CommandBarButton, IconButton, Stack } from '@fluentui/react';
import { ErrorCircleRegular } from '@fluentui/react-icons';
import { QuestionInput } from '../../components/QuestionInput';
import uuid from 'react-uuid';
import { messageStatus, agentMode } from '../bot/constants';
import { ChatHistoryPanel } from '../../components/ChatHistory/ChatHistoryPanel';
import { config } from '#/config';
import {  Group as PanelGroup, Panel, Separator } from 'react-resizable-panels';
import { FilePreview } from '#/components/FilePreviewPanel';

interface Props {
    isTeams: boolean
}

const project = ({ isTeams }: Props) => {
    // Router hooks for navigation and URL parameters
    const { agent } = useParams<{ agent: string }>();
    const { instance } = useMsal();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();   
    const conversationIdFromUrl = searchParams.get('conversationId');
    const [isHistoryLoading, setHistoryLoading] = useState(false);

    // Chat message state management
    const [messages, setMessages] = useState<BotResponse[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showLoadingMessage, setShowLoadingMessage] = useState<boolean>(false);
    const [isCitationPanelOpen, setIsCitationPanelOpen] = useState<boolean>(false);
    const [processMessages, setProcessMessages] = useState<messageStatus>(messageStatus.NotRunning);

    // Authentication and user state
    const [isInitialized, setIsInitialized] = useState(false);
    const [isAuthenticated, setAuthenticated] = useState(false);
    const [showAuthMessage, setShowAuthMessage] = useState<boolean | undefined>();
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [userId, setUserId] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [accessToken, setAccessToken] = useState<string>('');
    const [userRoles, setRoles] = useState<string[]>();
    const [tokenExpiry, setTokenExpiry] = useState(0);
    const [accessError, setAccessError] = useState<boolean>(false);
    const [resetMessageLoading, setResetMessageLoading] = useState<boolean>(false);
    const previousChatRef = useRef<any>(null);

    // Agent and conversation management
    const [AutomatedLabel, setAutomatedLabel] = useState(false);
    const [showLabel, setShowLabel] = useState(false);
    const [AutomatedAgent, setAutomatedAgent] = useState<string>('');
    const [conversationId, setconversationId] = useState<string>('');
    const [activeQuestion, setactiveQuestion] = useState<string>('');
    const [directLineToken, setDirectLineToken] = useState<string>('');
    const [availableOptions, setAvailableOptions] = useState<AvailableParameters>();
    const [activeCitation, setActiveCitation] = useState<Citation>();

    // UI and scrolling references
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const scrollableRef = useRef<HTMLDivElement | null>(null);
    const abortFuncs = useRef([] as AbortController[]);
    const insightsSchema = useRef<string[]>([]);

    // API request state
    const [fetchInputs, setFetchInputs] = useState<fetchInputs | undefined>(undefined);

    // App context for global state
    const appStateContext = useContext(AppStateContext)
    const AUTH_ENABLED = appStateContext?.state.frontendSettings?.auth_enabled;
    const agentRedirect = config.env.NEW_ARCHITECT_LIVE;

    // Variables for processing assistant messages
    let assistantMessage = {} as BotResponse
    let assistantContent = ""

    // Interface for text blocks in adaptive cards
    interface TextBlock { type: string; text: string; size: string, weight: string }

    // Load messages from history navigation state (fast path, no API call)
    useEffect(() => {
        if (!agent || !conversationId) return;

        const historyState = location.state as any;

        if (historyState?.messages && Array.isArray(historyState.messages)) {
            setconversationId(conversationId);
            localStorage.setItem("conversation_id", conversationId);

            if (historyState.dataSource) {
                if (isTeams) {
                    localStorage.setItem("dataSource", historyState.dataSource);
                } else {
                    sessionStorage.setItem("dataSource", historyState.dataSource);
                }
            }
            if (historyState.conversationTitle) {
                localStorage.setItem("title", historyState.conversationTitle);
            }

            const transformedMessages: BotResponse[] = historyState.messages.map((msg: any) => ({
                id: msg.id || uuid(),
                role: msg.role || 'assistant',
                content: msg.content || '',
                date: msg.createdAt || new Date().toISOString(),
                feedback: msg.feedback || undefined,
                type: msg.type || 'message',
                timestamp: msg.timestamp || '',
                channelId: msg.channelId || '',
                from: msg.from || { id: '', name: '' },
                conversation: msg.conversation || { id: conversationId, title: historyState.conversationTitle || '', messages: [], date: '' },
                text: msg.content || '',
                inputHint: msg.inputHint || '',
                context: msg.context || '',
                messageType: msg.messageType || 'text',
                card: msg.card || undefined,
                token: msg.token || directLineToken,
                streamUrl: msg.streamUrl || '',
                MessageCategory: msg.MessageCategory
            }));

            setMessages(transformedMessages);
            ScrollToBottom();
        }
    }, [agent, conversationId, location.state]);

    // Load messages by fetching history from the API when navigated with a conversationId in the URL
    useEffect(() => {
        if (!agent || !conversationIdFromUrl) return;

        setMessages([]);
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: null });

        if (accessToken && isInitialized) {
            setHistoryLoading(true);

            const fetchHistoryMessages = async () => {
                try {
                    const response = await historyRead(conversationIdFromUrl, '', '', accessToken) as any;
                    const historyMessages = Array.isArray(response) ? response : response?.messages;

                    if (historyMessages) {
                        const transformedMessages: BotResponse[] = historyMessages.map((msg: any) => ({
                            id: msg.id || uuid(),
                            role: msg.role || 'assistant',
                            content: msg.content || '',
                            date: msg.createdAt || new Date().toISOString(),
                            feedback: msg.feedback || undefined,
                            type: msg.type || 'message',
                            timestamp: msg.timestamp || '',
                            channelId: msg.channelId || '',
                            from: msg.from || { id: '', name: '' },
                            conversation: msg.conversation || { id: conversationIdFromUrl, title: '', messages: [], date: '' },
                            text: msg.content && typeof msg.content === 'string' ? msg.content : (msg.content?.body?.[0]?.text || ''),
                            inputHint: msg.inputHint || '',
                            context: msg.context || '',
                            messageType: msg.messageType || 'text',
                            card: msg.type === 'adaptiveCard' && msg.content ? msg.content : (msg.card || undefined),
                            token: msg.token || directLineToken,
                            streamUrl: msg.streamUrl || '',
                            MessageCategory: msg.MessageCategory
                        }));

                        setMessages(transformedMessages);
                        setconversationId(conversationIdFromUrl);
                        localStorage.setItem("conversation_id", conversationIdFromUrl);
                    }
                } catch (err) {
                    console.error('❌ Error fetching history:', err);
                } finally {
                    setHistoryLoading(false);
                }
            };

            fetchHistoryMessages();
        }
    }, [agent, conversationIdFromUrl, accessToken, isInitialized]);
    /**
     * Main initialization effect - sets up authentication and user session
     * Runs when AUTH_ENABLED changes to determine authentication method
     */
    useEffect(() => {
        const initialize = async () => {
            // Clear any existing data source information
            localStorage.removeItem("dataSource");
            sessionStorage.removeItem("dataSource");
            localStorage.removeItem("title");

            if (AUTH_ENABLED !== undefined) {
                localStorage.clear();
                // Choose authentication method based on platform
                if (isTeams) {
                    await getUserInfoListForTeams(); // Teams authentication flow
                } else {
                    await getUserInfoListForMsal(instance); // Browser MSAL authentication
                }
                localStorage.setItem('isSignInCodeReceived', JSON.stringify(true));
                setIsInitialized(true);
            }
        };
        initialize();
    }, [AUTH_ENABLED]);

    /**
     * Teams authentication handler
     * Fetches JWT token from backend and decodes user information
     */
    const getUserInfoListForTeams = async () => {
        try {
            const token = await getTeamsAuthToken(); // Get JWT from backend
            const claims = decodeToken(token); // Extract user claims from token
            if (!claims) {
                throw new Error("Invalid token.");
            }
            const userInfoList = createUserInfoList(token, claims);
            updateUserInfo(userInfoList, claims);
            setAuthenticated(true);
        } catch (error) {
            console.error("Error acquiring token or fetching user info for Teams:", error);
            setShowAuthMessage(true);
        }
    };

    /**
     * Browser MSAL authentication handler
     * Uses Microsoft Authentication Library for web browsers
     */
    const getUserInfoListForMsal = async (instance: any) => {
        // Iframe path: outside the MSAL try block to avoid TDZ conflict with inner const declarations
        try {
          if (window.self !== window.top) {
            const iframeToken = sessionStorage.getItem('akira_injected_token')
            if (iframeToken) {
              const iframeClaims = decodeToken(iframeToken)
              if (iframeClaims) {
                updateUserInfo(createUserInfoList(iframeToken, iframeClaims), iframeClaims)
                setAuthenticated(true)
              }
              return
            }
          }
        } catch { /* not in iframe */ }

        // CRITICAL: If in iframe but no token, fail gracefully instead of trying MSAL
        if (window.self !== window.top) {
          console.error('[IframeAuth] iframe detected but no injected token found. Cannot authenticate.')
          setShowAuthMessage(true)
          return
        }

        try {
            // Define required OAuth scopes
            const scopes = [
                `${config.env.REACT_APP_CLIENT_ID}/access_as_user`
            ];

            const account = instance.getActiveAccount(); // Check if user is already logged in
            if (!account) {
                throw new Error("No active account! Please log in.");
            }

            // Silently acquire access token
            const msalResponse = await instance.acquireTokenSilent({
                scopes: scopes,
                account: account,
            });

            const token = msalResponse.accessToken;
            const claims = msalResponse.idTokenClaims;
            const userInfoList = createUserInfoList(token, claims);

            // Set token expiry for renewal
            const newExpiry = msalResponse.expiresOn;
            setTokenExpiry(newExpiry.getTime());
            localStorage.setItem("tokenExpiry", newExpiry.getTime())
            localStorage.setItem("newExpiry", newExpiry)

            updateUserInfo(userInfoList, claims);
            setAuthenticated(true);
        } catch (error) {
            console.error("Error acquiring token or fetching user info for MSAL:", error);
            setShowAuthMessage(true);
        }
    };

    /**
     * Updates user information in state and localStorage
     * Extracts and stores user ID, email, access token, and roles
     */
    const updateUserInfo = (userInfoList: UserInfo[], claims: any) => {
        const emailClaim = claims.preferred_username;
        const userClaim = userInfoList[0]?.user_id;
        const accessToken = userInfoList[0]?.access_token;

        // Set state variables
        setUserId(userClaim);
        setEmail(emailClaim);
        setAccessToken(accessToken);

        // Store in localStorage for persistence
        setEmailInLocalStorage(emailClaim);
        setUserIdInLocalStorage(userClaim);
        setAccessTokenInLocalStorage(accessToken);

        // Extract and process user roles
        const rolesClaim = getClaimValue(userInfoList[0]?.user_claims, "roles");

        // Helper function to ensure roles is always an array
        const toArray = (value: string | string[] | null): string[] => {
            if (value === null) {
                return [];
            }
            return typeof value === 'string' ? [value] : value;
        };

        setRoles(toArray(rolesClaim));
    };

    /**
     * Determines if the new chat button should be disabled
     * Based on loading state and message count
     */
    const disabledButton = () => isLoading || (messages && messages.length === 0);

    /**
     * Initiates a new chat session
     * Currently empty but can be extended for chat reset functionality
     */
    const newChat = () => {
        setAutomatedAgent('');
        setShowLoadingMessage(false);
        setconversationId('');
        setIsLoading(false);
        setFetchInputs(undefined);
        if (isTeams) {
            localStorage.removeItem("dataSource");
        } else {
            sessionStorage.removeItem("dataSource");
        }
        localStorage.removeItem("title");
        localStorage.removeItem("historylistConId");
        setAvailableOptions(undefined);
        console.log("Closing Conversation.. Con ID =" + localStorage.getItem('conversationId'));
        // directLine.end();
        // GetDirectLineToken(true)
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: null });
        // agentRedirect ? window.location.assign(config.env.NEW_ARCHITECT_URL) : navigate('/');
    }

    const resetChat = async () => {
        messages.length = 0;
        setMessages([]);
        setconversationId('');
        setAvailableOptions(undefined);
        setFetchInputs(undefined);
        localStorage.removeItem("historylistConId");
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: null });

        const selectedagent = agents.find((a) => a.type === agent);
        if (selectedagent) {
            if (isTeams) {
                localStorage.setItem("dataSource", selectedagent.code);
            } else {
                sessionStorage.setItem("dataSource", selectedagent.code);
            }
            await handleCreateConversation(selectedagent.code);
        }
    };

    /**
     * Renders Microsoft Adaptive Cards in the chat interface
     * Handles special formatting for specific card types (e.g., OTP Statistics)
     * @param objAdaptiveCard - The adaptive card JSON object
     * @param conId - Conversation ID
     * @param messageId - Optional message ID for tracking
     */
    const renderAdaptiveCard = (objAdaptiveCard: any, conId: string, messageId?: string) => {
        // Create and configure adaptive card renderer
        const adaptiveCard = new AdaptiveCards.AdaptiveCard();
        adaptiveCard.parse(objAdaptiveCard);
        adaptiveCard.hostConfig = new AdaptiveCards.HostConfig({ supportsMarkdown: true });
        const renderedCard = adaptiveCard.render();

        // Special handling for OTP Statistics cards - adjust flex layout
        if (renderedCard && renderedCard.innerText.includes("OTP Statistics by Category")) {
            const elements = renderedCard.querySelectorAll('[style*="flex: 1 1 25%"]');
            elements.forEach(el => {
                (el as HTMLElement).style.flex = "1 1 30%";
            });
        }

        // Create BotResponse object for the adaptive card
        if (renderedCard?.hasChildNodes) {
            let response: BotResponse = {
                role: "assistant",
                card: objAdaptiveCard,
                feedback: undefined,
                type: 'adaptiveCard',
                id: messageId ?? '',
                timestamp: '',
                date: '',
                channelId: '',
                from: { id: '', name: '' },
                conversation: { id: '', title: '', messages: [], date: '' },
                text: '',
                inputHint: '',
                context: '',
                content: objAdaptiveCard,
                messageType: 'card',
                streamUrl: '',
                token: ''
            };

            // Extract text content from card body for display
            if (objAdaptiveCard.body) {
                const textBlock = objAdaptiveCard.body.find((item: TextBlock) => item.type === 'TextBlock');
                if (textBlock) {
                    response.text = textBlock.text;
                } else {
                    response.text = "image";
                }
            }

            // Check if card already exists to prevent duplicates
            const cardExists = messages.some(msg => JSON.stringify(msg.card) === JSON.stringify(objAdaptiveCard));

            if (!cardExists) {
                messages.push(response);
                setMessages([...messages]);
            }

            ScrollToBottom();
        }
    };

    /**
     * Processes incoming response messages from the API
     * Handles JSON parsing, citation extraction, and message formatting
     * @param message - Raw message content
     * @param convid - Conversation ID
     * @param messageId - Unique message identifier
     * @param category - Optional message category for filtering
     */
    const processResponse = (message: string, convid: string, messageId: string, category?: string) => {
        try {
            setShowLoadingMessage(true);
            setProcessMessages(messageStatus.Processing);

            if (!message) return;

            let toolMessage = {} as BotResponse;
            let citation: any = null;

            // Parse JSON messages for citations and references
            if (isJson(message)) {
                let ci = JSON.parse(message);

                // Handle both single citations and arrays
                if (Array.isArray(ci)) {
                    citation = parseCitations(ci);
                }
                else {
                    citation = parseCitations([ci]);
                }

                // Create tool message for citations
                toolMessage = {
                    id: messageId,
                    role: "tool",
                    content: citation,
                    date: new Date().toISOString(),
                    feedback: undefined,
                    type: 'message',
                    timestamp: '',
                    channelId: '',
                    from: {
                        id: '',
                        name: ''
                    },
                    conversation: {
                        id: '',
                        title: '',
                        messages: [],
                        date: ''
                    },
                    text: '',
                    inputHint: '',
                    context: '',
                    messageType: 'tool',
                    card: undefined,
                    token: directLineToken,
                    streamUrl: '',
                    MessageCategory: category
                };
            }

            // Create main response message
            let response: BotResponse = {
                role: "",
                id: messageId,
                text: message,
                context: citation,
                messageType: "text",
                card: undefined,
                feedback: undefined,
                type: 'message',
                timestamp: '',
                date: '',
                channelId: '',
                from: {
                    id: '',
                    name: ''
                },
                conversation: {
                    id: '',
                    title: '',
                    messages: [],
                    date: ''
                },
                inputHint: '',
                content: '',
                token: '',
                streamUrl: '',
                MessageCategory: category
            };

            // Set role based on whether it's a tool or assistant message
            if (toolMessage.content) {
                response.role = "tool";
            } else {
                response.role = "assistant";
            }
            response.id = messageId;
            response.text = message;
            response.context = citation;
            response.messageType = "text";

            // Retrieve the original question from localStorage
            let q: string = localStorage.getItem("question") ?? '';
            let id: string = uuid();

            // Find the most recent user message ID for proper threading
            try {
                if (messages.length > 0) {
                    let msg = [...messages].reverse().find((item) => item.role === 'user');
                    if (msg) {
                        id = msg.id;
                    }
                }
            } catch (e) { }

            // Create user message object for context
            const userMessage: BotResponse = {
                id: id,
                role: "user",
                content: q,
                date: new Date().toISOString(),
                feedback: undefined,
                type: 'message',
                timestamp: '',
                channelId: '',
                text: '',
                inputHint: '',
                context: '',
                from: {
                    id: userId,
                    name: ''
                },
                conversation: {
                    id: convid,
                    title: '',
                    messages: [],
                    date: '',
                },
                messageType: 'text',
                card: undefined,
                token: '',
                streamUrl: ''
            };

            // Process and add messages to the conversation
            processResultMessage(response, userMessage, toolMessage, '');
            ScrollToBottom();
        }
        catch (e) {
            console.error("error while processing response. Error message: ", e)
        }
        finally {
            setProcessMessages(messageStatus.Done);
            setShowLoadingMessage(false);
        }
    }

    /**
     * Processes and adds messages to the conversation thread
     * Handles message deduplication and proper message ordering
     * @param resultMessage - Main assistant response
     * @param userMessage - Original user question
     * @param toolMessage - Citation/tool information
     * @param conversationId - Current conversation ID
     */
    const processResultMessage = (resultMessage: BotResponse, userMessage: BotResponse, toolMessage: BotResponse, conversationId?: string) => {
        // Handle assistant messages and build content
        if (resultMessage.role === "assistant") {
            assistantContent += resultMessage.text
            assistantMessage = resultMessage
            assistantMessage.content = resultMessage.text
            assistantMessage.id = resultMessage.id
        }

        // Add tool messages (citations) if present
        if (toolMessage.content) {
            // ISSUE: Direct mutation - should use functional update
            messages.push(toolMessage);

            setMessages((prevMessages) => {
                const updatedMessages = [...prevMessages, toolMessage];
                console.log('Updated tool Messages:');
                return updatedMessages;
            });
            ScrollToBottom();
        }

        // Update existing message or add new one
        if (messages[messages.length - 1]?.id === assistantMessage.id) {
            messages[messages.length - 1].content = assistantMessage.content;
        }
        else {
            // Check for message existence to prevent duplicates
            const messageExists = messages.some(message => message.id === assistantMessage.id);

            if (!messageExists) {
                console.log('push message...');
                if (assistantMessage)
                    messages.push(assistantMessage);
            } else {
                console.log('Message with this ID already exists. Not pushing again.');
            }
        }

        setMessages(messages);
        ScrollToBottom();
        setProcessMessages(messageStatus.Done)
    }
    
    const handleHistoryItemRemoved = (item: BotConversation) => {
        // only reset the open chat if the deleted item is the one currently being viewed
        if (item.id === conversationId) {
            handleChatHistoryDeleted(isTeams, messages, setAvailableOptions, handleCreateConversation, resetChat);
        }
    }

      useEffect(() => {
        if (appStateContext?.state.currentChat) {
          const response = appStateContext.state.currentChat.messages as unknown as BotResponse[];
          const newMessages = [...response];
          const transformedMessages = newMessages.map(m => {
            return m;
          });
          setMessages(transformedMessages);
          ScrollToBottom();
        } else {
          setMessages([])
          ScrollToBottom();
        }
        setFetchInputs(undefined);
      }, [appStateContext?.state.currentChat]);

    useEffect(() => {
        const controller = new AbortController();
        if (fetchInputs != undefined) {
            setShowLoadingMessage(true);
            // setIsLoading(true);

            const run = async (controller: AbortController, inputs: fetchInputs) => {
                fetch(inputs.input, { ...inputs.init, signal: controller.signal })
                    .then(async (response) => await consumeTheResponse(response))
                    .catch((error) => {
                        if (error.name === 'AbortError') {
                            console.log('Fetch aborted');
                        } else {
                            console.error('Fetch error:', error);
                        }
                    })
                    .finally(() => {
                        setProcessMessages(messageStatus.Done);
                        // setIsLoading(false);
                        setShowLoadingMessage(false);
                        ScrollToBottom();
                        setFetchInputs(undefined);
                    });
            };

            run(controller, fetchInputs);
        }
        return () => {
            controller.abort();
        };
    }, [fetchInputs]);

    /**
     * Main effect to fetch available agents and handle project insights flow
     * Processes URL parameters for automated project selection
     * This is a complex flow that handles:
     * 1. Agent availability checking
     * 2. URL parameter parsing for project/country selection  
     * 3. Automated conversation setup
     * 4. Project validation and error handling
     */
    useEffect(() => {
        const fetchAgents = async () => {
            try {
                if (conversationIdFromUrl) return;
                const searchParams = new URLSearchParams(location.search);

                // Extract URL parameters for project insights
                const type = searchParams.get('type') ?? "";
                const country = searchParams.get('country') ?? "";
                const projectId = (searchParams.get('projectId')) ?? "";

                // Proceed only if user is authenticated
                if (accessToken) {
                    setIsLoading(true);
                    // Check what agents are available to this user
                    const availableAgents = await getListAvailableAgents(accessToken);
                    console.log(availableAgents);

                    // Block access if structured agent (project insights) is unavailable
                    if (availableAgents.agents_availability.structured === "unavailable") {
                        setIsLoading(false);
                        setErrorMessage("User don't have access to Analyze project data [GI]. Please contact your administrator to get access.");
                        setAccessError(true);
                    } else {
                        // User has access - proceed with agent setup
                        if (agents.some((a) => a.type === agent)) {
                            // Find and set the selected agent
                            const selectedagent = agents.find((a) => a.type === agent);
                            setAutomatedAgent(selectedagent?.name || '');
                            // setAutomatedLabel(true);
                            setShowLabel(true);

                            if (isTeams) {
                                localStorage.setItem("dataSource", selectedagent?.code || "");
                            } else {
                                sessionStorage.setItem("dataSource", selectedagent?.code || "");
                            }

                            // Create new conversation with the selected agent
                            const newConversation = await createNewConversationNew(selectedagent?.code || "", accessToken);

                            // Handle successful conversation creation
                            if (newConversation && newConversation.status !== 401) {
                                setconversationId(newConversation.conversation_id);
                                localStorage.setItem("conversation_id", newConversation.conversation_id);

                                // Render introduction adaptive card if provided
                                if (newConversation.adaptive_card) {
                                    renderAdaptiveCard(JSON.parse(newConversation.adaptive_card), newConversation.conversation_id);
                                }

                                // Process welcome message if provided
                                if (newConversation.message) {
                                    processResponse(newConversation.message, newConversation.conversation_id ?? '', '', 'greeting')
                                }

                                // Store available parameters for dropdown options
                                if (newConversation.available_parameters) {
                                    setAvailableOptions(newConversation.available_parameters || null);
                                }
                            }


                            // Decode URL parameters for project selection
                            const decodedType = decodeURIComponent(type);
                            const decodedCountry = decodeURIComponent(country);
                            const decodedP_Id = decodeURIComponent(projectId);

                            setIsLoading(false)
                            // setShowLoadingMessage(true);

                            if (decodedType && decodedCountry && decodedP_Id) {
                                // Find matching project in available options
                                const countryArray = Object.keys(newConversation?.available_parameters?.project_insights?.value || {});
                                const countryOptions: [string, string][] = await newConversation?.available_parameters?.project_insights?.value?.[decodedCountry] ?? [];
                                const matchingProject = countryOptions.find(p => p[1] === decodedP_Id);
                                const projectName = matchingProject ? matchingProject[0] : undefined;

                                setShowLoadingMessage(true);

                                // Handle invalid project parameters
                                if (!projectName) {
                                    const errorMsg: BotResponse = {
                                        id: uuid(),
                                        role: "error",
                                        content: `Invalid query parameters provided: country=${country}, project_Id=${decodedP_Id}, Please check the URL.`,
                                        date: new Date().toISOString(),
                                        card: undefined,
                                        feedback: undefined,
                                        type: '',
                                        timestamp: '',
                                        channelId: '',
                                        from: {
                                            id: '',
                                            name: ''
                                        },
                                        conversation: {
                                            id: '',
                                            title: '',
                                            messages: [],
                                            date: ''
                                        },
                                        text: '',
                                        inputHint: '',
                                        context: '',
                                        messageType: '',
                                        token: '',
                                        streamUrl: ''
                                    };
                                    setMessages([...messages, errorMsg]);
                                    setShowLoadingMessage(false);
                                    ScrollToBottom();
                                    return
                                }

                                // Create adaptive card showing selected country and project
                                const countryCard = {
                                    type: "adaptiveCard",
                                    body: [
                                        {
                                            type: "TextBlock",
                                            text: `Please select a Country:`,
                                            size: "Medium",
                                            weight: "Bolder"
                                        },
                                        {
                                            type: 'Input.ChoiceSet',
                                            id: 'countryChoiceSet',
                                            style: 'compact',
                                            choices: countryArray.map(country => ({
                                                title: country,
                                                value: country
                                            })),
                                            isMultiSelect: false,
                                            value: decodedCountry,
                                            isRequired: true,
                                            errorMessage: "Please select a country specific project",
                                            name: 'ProjectsChoiceSet'
                                        },
                                    ],
                                    actions: [
                                        {
                                            type: 'Action.Submit',
                                            title: 'Submit',
                                        }
                                    ],
                                    $schema: "http://adaptivecards.io/schemas/adaptive-card.json",
                                    version: "1.5"
                                }

                                renderAdaptiveCard(countryCard, newConversation.conversation_id);

                                // Create adaptive card for project selection
                                const projectCard = {
                                    type: "AdaptiveCard",
                                    body: [
                                        {
                                            type: "TextBlock",
                                            text: `You selected ${decodedCountry} as a Country. Please select a Project:`,
                                            size: "Medium",
                                            weight: "Bolder"
                                        },
                                        {
                                            type: 'Input.ChoiceSet',
                                            id: 'projectChoiceSet',
                                            style: 'compact',
                                            choices: countryOptions.map(option => ({
                                                title: option[0],
                                                value: JSON.stringify(option)
                                            })),
                                            isMultiSelect: false,
                                            value: JSON.stringify([projectName, decodedP_Id]), // Pre-select the project
                                            isRequired: true,
                                            errorMessage: "Please select a project",
                                            name: 'ProjectsChoiceSet'
                                        }
                                    ],
                                    actions: [
                                        {
                                            type: 'Action.Submit',
                                            title: 'Submit',
                                        }
                                    ],
                                    $schema: "http://adaptivecards.io/schemas/adaptive-card.json",
                                    version: "1.5"
                                };

                                // Render the project selection adaptive card
                                renderAdaptiveCard(projectCard, newConversation.conversation_id);

                                setShowLoadingMessage(false);

                                // Prepare selections for API call
                                const selections = [project_insight_options[1].functionName, decodedCountry, JSON.stringify([projectName, decodedP_Id])];

                                // Create user message showing what was selected
                                const userMessage: BotResponse = {
                                    id: uuid(),
                                    role: "user",
                                    content: `PROJECT_INSIGHTS : ${projectName} > ${decodedP_Id}.`,
                                    date: new Date().toISOString(),
                                    feedback: undefined,
                                    type: 'message',
                                    timestamp: '',
                                    channelId: '',
                                    from: {
                                        id: userId,
                                        name: ''
                                    },
                                    conversation: {
                                        id: newConversation.conversation_id,
                                        title: '',
                                        messages: [],
                                        date: '',
                                    },
                                    text: '',
                                    inputHint: '',
                                    context: '',
                                    messageType: 'text',
                                    card: undefined,
                                    token: directLineToken,
                                    streamUrl: ''
                                };

                                // ISSUE: Direct mutation - should use functional update
                                setMessages([...messages, userMessage]);

                                // Execute the project insights API call
                                setShowLoadingMessage(true);
                                const result = await makeApiRequestPayload(newConversation.conversation_id, selections);
                                console.log('API result :>> ', result);
                                setShowLoadingMessage(false);
                                ScrollToBottom();
                            } else {
                                setShowLoadingMessage(false);
                            }
                        } else {
                            // Agent not found - redirect to home
                            setIsLoading(false);
                            setErrorMessage("Agent not found. Please check the URL.");
                            setAccessError(true);
                        }
                        // setIsLoading(false);
                    }
                }
            } catch (err) {
                setErrorMessage("Invalid query parameters provided. Please check the URL.");
                setShowLoadingMessage(false);
                setAccessError(true);
                console.error(err);
            }
        };
        fetchAgents();
    }, [accessToken, conversationIdFromUrl])

    /**
     * Scrolling effect - ensures chat scrolls to bottom when messages change
     * Uses smooth scrolling behavior for better UX
     */
    useEffect(() => {
        if (chatMessageStreamEnd?.current) {
            chatMessageStreamEnd?.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    /**
     * Layout effect for scrolling during loading states
     * Triggers scroll when loading message appears or processing status changes
     */
    useLayoutEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" })
    }, [showLoadingMessage, processMessages, fetchInputs]);

    /**
     * Helper function to extract claim values from user token
     * @param userClaims - Array of user claims from JWT
     * @param claimType - Type of claim to extract (e.g., "roles")
     * @returns Claim value or null if not found
     */
    const getClaimValue = (userClaims: any[], claimType: string): string | string[] | null => {
        const claim = userClaims?.find((claim: { type: string; value: string | string[] }) => claim.type === claimType);
        return claim ? claim.value : null;
    };

    /**
     * Manual scroll to bottom function
     * Used for programmatic scrolling after message updates
     */
    const ScrollToBottom = () => {
        const container = scrollableRef.current;
        if (chatMessageStreamEnd.current && container) {
            chatMessageStreamEnd.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const updateConversationIdInLocal = (conversation_id: string) => {
        setconversationId(conversation_id);
        localStorage.setItem("conversation_id", conversation_id);
    }

    /**
     * Handles form submissions from adaptive cards
     * Routes different submission types to appropriate handlers
     * @param data - Form data from adaptive card submission
     */
    const handleSubmit = async (data: any) => {
        // Handle function buttons (portfolio/project insights)
        if (data.function === project_insight_options[0].functionName || data.function === project_insight_options[1].functionName) {
            ButtonClick(data.function, availableOptions)
        }
        // Handle multi-level dropdown selections (choiceSet0-6)
        else if (data.choiceSet0) {
            const selectedOption = data.choiceSet0
            console.log('selectedOption :>> ', selectedOption);
            await handleSelection(1, selectedOption, [data.selectedOption])
        } else if (data.choiceSet1) {
            const selectedOption = data.choiceSet1
            await handleSelection(2, selectedOption, data.previousSelections)
        } else if (data.choiceSet2) {
            const selectedOption = data.choiceSet2
            await handleSelection(3, selectedOption, data.previousSelections)
        } else if (data.choiceSet3) {
            const selectedOption = data.choiceSet3
            await handleSelection(4, selectedOption, data.previousSelections)
        } else if (data.choiceSet4) {
            const selectedOption = data.choiceSet4
            await handleSelection(5, selectedOption, data.previousSelections)
        } else if (data.choiceSet5) {
            const selectedOption = data.choiceSet5
            await handleSelection(6, selectedOption, data.previousSelections)
        } else if (data.choiceSet6) {
            const selectedOption = data.choiceSet6
            await handleSelection(7, selectedOption, data.previousSelections)
        } else {
            // Handle direct function calls or text input
            if (data.function)
                await makeApiRequestPayload(conversationId, [data.function]);
            else
                makeApiRequestWithCosmosDB(data, conversationId, 1)
            ScrollToBottom()
        }
    }

    const consumeTheResponse = async (response: Response) => {
        if (!response.body) {
            throw new Error('ReadableStream not supported or no data!');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';

        const sanitizeLatex = (text: string): string => {
            text = text.replace(/\$\$([\s\S]+?)\$\$/g, (match) => match);

            text = text.replace(/\\\[((?:.|\n)*?)\\\]/g, (match) => match);

            text = text.replace(/\\\(((?:.|\n)*?)\\\)/g, (match) => match);

            return text;
        };

        const tryExtractJson = (input: string): [any[], string] => {
            const results: any[] = [];
            let remaining = input;

            while (true) {
                const start = remaining.indexOf('{');
                if (start === -1) break;

                let inString = false;
                let escape = false;
                let braceCount = 0;
                let end = -1;

                for (let i = start; i < remaining.length; i++) {
                    const char = remaining[i];

                    if (escape) {
                        escape = false;
                    } else if (char === '\\') {
                        escape = true;
                    } else if (char === '"') {
                        inString = !inString;
                    } else if (!inString) {
                        if (char === '{') braceCount++;
                        else if (char === '}') braceCount--;
                    }

                    if (braceCount === 0 && !inString) {
                        end = i;
                        break;
                    }
                }

                if (end === -1) break;

                const candidate = remaining.slice(start, end + 1);

                try {
                    const parsed = JSON.parse(candidate);
                    results.push(parsed);
                    remaining = remaining.slice(end + 1);
                } catch {
                    break;
                }
            }

            return [results, remaining];
        };

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });

            const [parsedObjects, remaining] = tryExtractJson(buffer);
            buffer = remaining;

            for (const parsed of parsedObjects) {
                if (parsed.message) {
                    const sanitized = sanitizeLatex(parsed.message);
                    processResponse(sanitized, parsed.conversation_id, parsed.msg_id);
                    updateConversationIdInLocal(parsed.conversation_id);
                }

                if (parsed.adaptive_card) {
                    if (Object.keys(parsed.adaptive_card).length > 0) {
                        renderAdaptiveCard(parsed.adaptive_card, parsed.conversation_id, parsed.msg_id);
                    } else {
                        processResponse(parsed.message, parsed.conversation_id, parsed.msg_id);
                    }
                }
            }
        }
    };

    /**
     * Creates and renders adaptive cards for multi-level dropdown selection
     * Handles the complex dropdown hierarchy for portfolio/project insights
     * @param data - Selected option or function name
     * @param availableParameters - Available options for current level
     * @param level - Current dropdown level (0-based)
     * @param previousSelections - Array of previous selections in hierarchy
     * @param schemaLevel - Schema level for label generation
     */
    const ButtonClick = (
        data: any,
        availableParameters: KeyValuePair = {},
        level = 0,
        previousSelections: string[] = [],
        schemaLevel: number = 0
    ) => {
        // console.log('ButtonClick data :>> ', data);
        // console.log('ButtonClick availableParameters :>> ', availableParameters);
        // console.log('ButtonClick level :>> ', level);
        // console.log('ButtonClick previousSelections :>> ', previousSelections);

        // Set up schema based on function type
        if (data === project_insight_options[1].functionName)
            insightsSchema.current = availableOptions?.project_insights?.schema || [];

        if (data === project_insight_options[0].functionName)
            insightsSchema.current = availableOptions?.portfolio_insights?.schema || [];

        // Determine available options based on function type
        let options: {} =
            data === project_insight_options[1].functionName
                ? availableOptions?.project_insights?.value || {}
                : data === project_insight_options[0].functionName
                    ? availableOptions?.portfolio_insights?.value || {}
                    : availableParameters

        // Convert options to choice format for adaptive cards
        let choices: ChoicesChoiceSet[] = Array.isArray(options)
            ? options.map(x => {
                return Array.isArray(x)
                    ? { title: x[0], value: JSON.stringify(x) } // Project Insights with [name, id] pairs
                    : { title: x, value: x }
            })
            : Object.keys(options).map(x => {
                return { title: x, value: x }
            })

        // Create adaptive card for dropdown selection
        const adaptiveCard = {
            type: 'AdaptiveCard',
            body: [
                {
                    id: uuid(),
                    type: 'TextBlock',
                    text: getDropdownLabel(previousSelections, insightsSchema.current, level, schemaLevel),
                    weight: 'bolder',
                    size: 'medium'
                },
                {
                    type: 'Input.ChoiceSet',
                    id: `choiceSet${level}`,
                    style: 'compact',
                    choices: choices,
                    isMultiSelect: false,
                    value: data,
                    isRequired: true,
                    errorMessage: "Please select an option",
                    name: `choiceSet_${level}_${choices[0]?.value || 'default'}`
                }
            ],
            actions: [
                {
                    type: 'Action.Submit',
                    title: 'Submit',
                    data: { level: level, previousSelections: previousSelections, selectedOption: data }
                }
            ],
            $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
            version: '1.5'
        }

        // Render card if options are available, otherwise show error
        if (availableOptions != null && Object.keys(availableOptions).length !== 0)
            renderAdaptiveCard(adaptiveCard, conversationId)
        else {
            // Show error for historical conversations
            const assistantMessage: BotResponse = {
                id: uuid(),
                role: "error",
                content: "INFO: This function is unavailable due to it being invoked for the historical conversation. To use this button please start a new conversation with the Agent.",
                date: new Date().toISOString(),
                feedback: undefined,
                type: "message",
                timestamp: '',
                channelId: '',
                from: {
                    id: '',
                    name: ''
                },
                conversation: {
                    id: '',
                    title: '',
                    messages: [],
                    date: '',
                },
                text: '',
                inputHint: '',
                context: '',
                messageType: 'error',
                card: undefined,
                token: directLineToken,
                streamUrl: ''
            };
            // ISSUE: Direct mutation of messages array
            messages.push(assistantMessage);
        }
    }

    /**
     * Makes API request with selected parameters for portfolio/project insights
     * Formats the request payload based on function type and parameters
     * @param convId - Conversation ID
     * @param selections - Array of user selections from dropdowns
     */
    const makeApiRequestPayload = async (convId: string, selections: string[]) => {
        setShowLoadingMessage(true);

        const url = `${config.env.API_URL}/${convId}/post_function`
        const timestamp = getCurrentTimestamp()
        const func = selections[0]
        let parameters: PortfolioInsightsPayload | {} = {}

        // Format parameters based on function type
        if (func === project_insight_options[0].functionName) {
            // Portfolio Insights parameters
            parameters = {
                'Global Product Group': selections[1],
                Region: selections.hasOwnProperty(2) && selections[2] !== undefined ? selections[2] : '',
                Country: selections.hasOwnProperty(3) && selections[3] !== undefined ? selections[3] : ''
            };
        }

        if (func === project_insight_options[1].functionName) {
            // Project Insights parameters  
            parameters = {
                Project: (selections.length > 1) ? JSON.parse(selections[2]) : ''
            };
        }

        // Create API request payload
        const payload: ApiPostFunctionRequest = {
            function: func,
            parameters,
            timestamp
        };

        // Create user-friendly label for the selection
        let label = `${func}`;
        if (selections.length > 1) {
            if (func === project_insight_options[1].functionName) {
                label += ` : ${JSON.parse(selections[2]).join(' > ')}.`;
            } else {
                label += ` : ${selections.slice(1).join(' > ')}.`;
            }
        } else {
            label += `.`;
        }

        // Create user message showing what was requested
        const userMessage: BotResponse = {
            id: uuid(),
            role: 'user',
            content: label,
            date: new Date().toISOString(),
            feedback: undefined,
            type: 'message',
            timestamp: '',
            channelId: '',
            from: {
                id: '',
                name: ''
            },
            conversation: {
                id: '',
                title: '',
                messages: [],
                date: '',
            },
            text: '',
            inputHint: '',
            context: '',
            messageType: 'text',
            card: undefined,
            token: directLineToken,
            streamUrl: ''
        };

        messages.push(userMessage);

        // Set up API request
        setFetchInputs({
            input: url,
            init: {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            }
        });
    }

    /**
     * Sends a simple message to the conversation
     * Used for text-based interactions
     * @param convId - Conversation ID
     * @param message - Message content
     */
    const SendMessage = (convId: string, message: string) => {
        let userMessage: UserMessage = {
            user_message: message,
            timestamp: getCurrentTimestamp()
        };
        const url = `${config.env.API_URL}/${convId}/messages`;

        setFetchInputs({
            input: url,
            init: {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(userMessage),
            }
        });
    }

    // Type definition for question input (string or dropdown object)
    type QuestionType = string | { action: string, dropdownChoices: string };

    /**
     * Makes API request for general chat messages (non-function calls)
     * Handles both text input and dropdown selections
     * @param question - User question or dropdown selection
     * @param convId - Conversation ID  
     * @param ptr - Pointer indicating message position (0 or 1)
     */
    const makeApiRequestWithCosmosDB = async (question: QuestionType, convId: string, ptr: number) => {
        console.log(question, convId, ptr);
        setShowLoadingMessage(true);

        // Helper to extract question value from object or string
        const getQuestionValue = (question: any): string => {
            if (typeof question === 'object' && question !== null && 'dropdownChoices' in question) {
                return question;
            }
            return question;
        };

        const processedQuestion = getQuestionValue(question);

        ScrollToBottom();
        setactiveQuestion(processedQuestion);
        localStorage.setItem("question", processedQuestion);

        // Set up abort controller for request cancellation
        const abortController = new AbortController();
        abortFuncs.current.unshift(abortController);

        // Check if this is a dropdown question vs regular text
        const isDropdownQuestion = typeof question === 'object' && question !== null && 'dropdownChoices' in question;

        // Create user message object
        const userMessage: BotResponse = {
            id: uuid(),
            role: 'user',
            content: processedQuestion,
            date: new Date().toISOString(),
            feedback: undefined,
            type: 'message',
            timestamp: '',
            channelId: '',
            from: {
                id: '',
                name: ''
            },
            conversation: {
                id: '',
                title: '',
                messages: [],
                date: '',
            },
            text: '',
            inputHint: '',
            context: '',
            messageType: 'text',
            card: undefined,
            token: directLineToken,
            streamUrl: ''
        };

        // Add user message to chat (only for non-dropdown questions on first call)
        if (!isDropdownQuestion) {
            if (ptr === 1) {
                messages.push(userMessage);
            }
            const newMessages = [...messages];

            // Transform messages (currently just passes through)
            const transformedMessages = newMessages.map((m) => {
                return m;
            });

            setMessages(transformedMessages);
        }

        try {
            // Handle existing conversation context from app state
            if (appStateContext?.state.currentChat?.id) {
                sessionStorage.setItem("DBconversationId", appStateContext.state.currentChat.id);

                if (appStateContext.state.currentChat.messages[0].conversation["id"]) {
                    setDirectLineToken(appStateContext.state.currentChat.token);
                }
            }

            // Send message to appropriate conversation
            if (ptr === 1) {
                if (!appStateContext?.state.currentChat?.id) {
                    SendMessage(convId, processedQuestion);
                } else {
                    const conversId = appStateContext?.state.currentChat?.id === undefined || appStateContext?.state.currentChat === undefined ? convId : appStateContext?.state.currentChat?.id;
                    SendMessage(conversId, processedQuestion);
                }
            }

            // Store messages in localStorage for persistence
            const jsonString = JSON.stringify(messages);
            localStorage.setItem("messages", jsonString);

            setProcessMessages(messageStatus.Processing);
        } catch (e) {
            if (!(e instanceof SyntaxError)) {
                console.error(e);
                throw e;
            } else {
                console.log("Incomplete message. Continuing...");
            }
        }
    };

    /**
     * Handles multi-level dropdown selection navigation
     * Manages the complex hierarchy traversal for portfolio/project insights
     * @param level - Current selection level
     * @param selectedOption - User's selection at current level
     * @param previousSelections - Array of all previous selections
     */
    const handleSelection = async (level: number, selectedOption: string, previousSelections: string[]) => {
        let nextAvailableOptions: any
        const schemaLevel: number = level;

        // console.log('handleSelection selectedOption :>> ', selectedOption);
        // console.log('handleSelection previousSelections :>> ', previousSelections);
        // console.log('handleSelection level :>> ', level);

        // Handle first level selection (choosing function type)
        if (previousSelections.length === 0) {
            nextAvailableOptions =
                selectedOption === project_insight_options[1].functionName
                    ? availableOptions?.project_insights?.value || {}
                    : selectedOption === project_insight_options[0].functionName
                        ? availableOptions?.portfolio_insights?.value || {}
                        : {}
        } else {
            // Handle subsequent level selections - traverse the option hierarchy

            // Determine which mode we're in based on previous selections
            const mode = previousSelections.includes(project_insight_options[1].functionName)
                ? agentMode.PROJECT_INSIGHTS
                : previousSelections.includes(project_insight_options[0].functionName)
                    ? agentMode.PORTFOLIO_INSIGHTS
                    : agentMode.UNDEFINED;

            // Get base options for the current mode
            const options = mode === agentMode.PROJECT_INSIGHTS
                ? availableOptions?.project_insights?.value || {}
                : mode === agentMode.PORTFOLIO_INSIGHTS
                    ? availableOptions?.portfolio_insights?.value || {}
                    : {}

            // Navigate through the option hierarchy based on previous selections
            while (true) {
                nextAvailableOptions = { ...options };

                // Traverse down the hierarchy using previous selections as keys
                if (previousSelections.length > 1) {
                    for (let index = 1; index < previousSelections.length; index++) {
                        const elements = Object.entries(nextAvailableOptions);
                        const element = elements.find((value) => value[0] === previousSelections[index]);

                        nextAvailableOptions = element?.[1];
                    }
                }

                // Get next level options using current selection
                nextAvailableOptions = nextAvailableOptions[selectedOption]

                // If we reach a string value, we've hit a leaf node
                if (typeof nextAvailableOptions === 'string') {
                    selectedOption = nextAvailableOptions;
                    nextAvailableOptions = undefined;
                    break;
                }

                // Check if we need to continue auto-selecting
                // Portfolio insights has more than 2 options, project insights has more than 1
                if (nextAvailableOptions === undefined ||
                    (mode === agentMode.PORTFOLIO_INSIGHTS && Object.keys(nextAvailableOptions).length > 2) ||
                    (mode === agentMode.PROJECT_INSIGHTS && Object.keys(nextAvailableOptions).length > 1))
                    break;

                // Auto-select the next option if only one choice available
                previousSelections = [...previousSelections, selectedOption];
                selectedOption = Object.keys(nextAvailableOptions)[1];
                level++;
            }
        }

        // Determine next action based on whether more options are available
        if (nextAvailableOptions !== undefined && Object.keys(nextAvailableOptions).length > 0) {
            // More options available - show next dropdown
            ButtonClick(selectedOption, nextAvailableOptions, level++, [...previousSelections, selectedOption], schemaLevel)
        } else {
            // Final selection - make API request
            if (previousSelections.length === 0)
                makeApiRequest(selectedOption, conversationId);
            else
                await makeApiRequestPayload(conversationId, [...previousSelections, selectedOption]);
        }
    }

    /**
     * Makes a general API request for chat functionality
     * Handles data source determination and conversation setup
     * @param prompt - User's message/question
     * @param conversationId - Current conversation ID
     */
    const makeApiRequest = async (prompt: string, conversationId: string) => {
        setShowLoadingMessage(true);
        console.log(prompt, conversationId);

        // Get data source from storage (Teams uses localStorage, browser uses sessionStorage)
        let datasource = (isTeams ? localStorage.getItem("dataSource") : sessionStorage.getItem("dataSource")) ?? '';

        console.log("Datasource from storage: ", datasource);
        // Set up data source if not already configured
        if (datasource === '') {
            // Auto-detect if this is the automated agent prompt
            if (prompt.trim() === AutomatedAgent) {
                localStorage.setItem("title", AutomatedAgent);
                datasource = "structured";
            }

            // Store data source based on platform
            if (isTeams) {
                localStorage.setItem("dataSource", datasource);
            } else {
                sessionStorage.setItem("dataSource", datasource);
            }

            console.log(datasource)

            // Handle structured data source
            if (datasource === 'structured') {
                handleCreateConversation(datasource);
                makeApiRequestWithCosmosDB(prompt, conversationId, 0);
            }
        }
        else {
            // Data source already set - proceed with request
            makeApiRequestWithCosmosDB(prompt, conversationId, 1);
        }

        // console.log(prompt);
    }

    /**
     * Creates a new conversation with specified agent
     * Sets up the conversation context and initial cards/messages
     * @param agent - Agent code/type to create conversation with
     * @returns Promise<boolean> - Success status
     */
    const handleCreateConversation = async (agent: string): Promise<any> => {
        try {
            setShowLoadingMessage(true);
            const authHeader = accessToken;
            const newConversation = await createNewConversationNew(agent, authHeader);
            // console.log(newConversation);

            if (newConversation && newConversation.status !== 401) {
                // Set conversation ID for future requests
                setconversationId(newConversation.conversation_id);
                localStorage.setItem("conversation_id", newConversation.conversation_id);

                // Render introduction adaptive card
                if (newConversation.adaptive_card) {
                    renderAdaptiveCard(JSON.parse(newConversation.adaptive_card), newConversation.conversation_id);
                }

                // Process welcome message
                if (newConversation.message) {
                    processResponse(newConversation.message, newConversation.conversation_id ?? '', '', 'greeting')
                }

                // Store available options for dropdowns
                if (newConversation.available_parameters) {
                    setAvailableOptions(newConversation.available_parameters || null);
                }

                return true;
            }
        } catch (err) {
            console.error(err);
            return false;
        }
        finally {
            setShowLoadingMessage(false);
        }
    };

    /**
     * Handles clicking on the automated agent label
     * Resets the chat and clears the automated label
     */
    const handleAgentLabelClick = () => {
        setIsLoading(true);
        setShowLoadingMessage(true);

        newChat();
        setAutomatedAgent("");
        // setAutomatedLabel(false);
        setShowLabel(false);

        setIsLoading(false);
        setShowLoadingMessage(false);
    }

    if (accessError) {
        return (
            <div className={styles.accessError}>
                {errorMessage}
            </div>
        )
    }

    // Loading state - show loading screen while initializing
    if (isLoading) {
        return (
            <div className={styles.accessError}>Loading....</div>
        )
    }

    const handleCloseCitationPanel = () => {
        setIsCitationPanelOpen(false);
        setActiveCitation(undefined);
    }

    const showCitationPanel = !!(messages && messages.length > 0 && isCitationPanelOpen && activeCitation);
    const showHistoryPanel = !!(appStateContext?.state.isChatHistoryOpen && appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured);

    return (
        <div className={styles.container}>
        <PanelGroup orientation="horizontal" style={{ width: '100%', height: '100%' }}>
            <Panel defaultSize="100%" minSize="40%" className={styles.chatContainer} style={{height:`${agentRedirect ? "100vh" : "calc(100vh - 100px)"}`}}>
            {isHistoryLoading ? (
                <div style={{ margin: 'auto', fontSize: 'x-large', fontWeight: 600 }}>Loading Chat history...</div>
            ) :(
            <>
                <div className={styles.chatMessageStream} style={{ marginBottom: isLoading ? "40px" : "5px" }} role="log">
                    {
                        messages.map((answer, index) => (
                            <div key={index}>
                                {
                                    answer.role === 'user' ?
                                        <div className={styles.chatMessageUser} tabIndex={0}>
                                            {
                                                answer.messageType === 'card' ?
                                                    <AdaptiveCardRenderer
                                                        card={answer.card}
                                                        onSubmit={handleSubmit}
                                                        isLoading={(showLoadingMessage || isLoading || fetchInputs !== undefined)}
                                                    />
                                                    : <div className={styles.chatMessageUserMessage}>{answer.content}</div>
                                            }
                                        </div> :
                                        (
                                            answer.role === 'assistant' ?
                                                <div>
                                                    {
                                                        answer.type === 'adaptiveCard' && answer.content ?
                                                            (
                                                                <div>
                                                                    <img className={styles.UserProfile} src={AkiraPictogram} alt="Avatar"></img>
                                                                    <div className={styles.chatMessageGpt}
                                                                        key={answer.id}
                                                                        id={`message-${answer.id}`}
                                                                    >
                                                                        <AdaptiveCardRenderer messages={messages} messageId={answer.id} card={answer.content} onSubmit={handleSubmit} isLoading={(showLoadingMessage || isLoading || fetchInputs !== undefined)} />
                                                                    </div>
                                                                </div>
                                                            ) : answer.type === 'message' ?
                                                                (
                                                                    <div>
                                                                        <img className={styles.UserProfile} src={AkiraPictogram} alt="Avatar"></img>
                                                                        <div
                                                                            key={answer.id}
                                                                            id={`message-${answer.id}`}
                                                                            className={styles.chatMessageGpt}
                                                                        >
                                                                            <Answer
                                                                                answer={{
                                                                                    answer: answer.content,
                                                                                    citations: parseCitationFromMessage(messages[index + 1]),
                                                                                    message_id: answer.id,
                                                                                    feedback: answer.feedback,
                                                                                    order_id: answer.orderId,
                                                                                    MessageCategory: answer.MessageCategory
                                                                                }}
                                                                                onCitationClicked={() => null}
                                                                                messages={messages}
                                                                                selectedAgent={AutomatedAgent}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                ) : null
                                                    }
                                                </div> :
                                                answer.role === "error" ?
                                                    (
                                                        <div className={styles.botError}>
                                                            <img className={styles.UserProfile} src={AkiraPictogram} alt="Avatar"></img>
                                                            <div className={styles.chatMessageError}>
                                                                <Stack horizontal className={styles.chatMessageErrorContent}>
                                                                    <ErrorCircleRegular className={styles.errorIcon} style={{ color: "rgba(182, 52, 67, 1)" }} />
                                                                    <span>Error</span>
                                                                </Stack>
                                                                <span className={styles.chatMessageErrorContent}>{answer.content}</span>
                                                            </div>
                                                        </div>
                                                    ) : null
                                        )}
                            </div>
                        ))}

                    {
                        showLoadingMessage && (
                            <div>
                                <img className={styles.UserProfile} src={AkiraPictogram} alt="Avatar"></img>
                                <div className={styles.chatMessageGpt}>
                                    <Answer
                                        answer={{
                                            answer: resetMessageLoading ?"Resetting chat...": "Generating answer...",
                                            citations: [],
                                            order_id: 0,
                                            MessageCategory: undefined
                                        }}
                                        onCitationClicked={() => null}
                                        messages={[]}
                                        selectedAgent={AutomatedAgent}
                                    />
                                </div>
                            </div>
                        )}
                    <div ref={chatMessageStreamEnd} />
                </div>
                <Stack horizontal className={styles.chatInput}>
                    <Stack tokens={{ childrenGap: 10 }}>
                        {
                            appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured &&
                            <div id="newChat" title='Start a new chat'>
                                <CommandBarButton
                                    role="button"
                                    styles={{
                                        icon: {
                                            color: '#FFFFFF',
                                        },
                                        iconDisabled: {
                                            color: "#BDBDBD !important"
                                        },
                                        root: {
                                            color: '#FFFFFF',
                                            background: "radial-gradient(109.81% 107.82% at 100.1% 90.19%, #eb0023 33.63%, #eb0023 70.31%, #eb0023 100%)"
                                        },
                                        rootDisabled: {
                                            background: "#F0F0F0"
                                        }
                                    }}
                                    className={styles.newChatIcon}
                                    iconProps={{ iconName: 'Add' }}
                                    onClick={async () => {
                                        setResetMessageLoading(true);
                                        await resetChat();
                                        setResetMessageLoading(false);
                                    }}
                                    disabled={disabledButton()}
                                    aria-label="start a new chat button"
                                />
                            </div>
                        }
                    </Stack>
                    <QuestionInput
                        clearOnSend
                        placeholder="Type a new question..."
                        disabled={isLoading || accessError}
                        selectedAgent={AutomatedAgent || ""}
                        // automatedLabel={AutomatedLabel}
                        handleLabelClick={handleAgentLabelClick}
                        showLabel={Boolean(AutomatedAgent && showLabel)}
                        // showLabel={Boolean(AutomatedAgent && AutomatedLabel) || showLabel}
                        onSend={(question, id) => {
                            appStateContext?.state.isCosmosDBAvailable?.cosmosDB ? makeApiRequest(question, conversationId) : makeApiRequest(question, conversationId)
                        }}
                        conversationId={conversationId}
                    />
                </Stack>
                </>
            )}
            </Panel>
            {
                showCitationPanel && (
                <>
                    <Separator className={styles.resizeHolder}/>
                    <Panel defaultSize="40%" minSize="30%">
                        <FilePreview citation={activeCitation} onCloseClick={handleCloseCitationPanel} onTitleClick={onViewSource} agent={AutomatedAgent} />
                    </Panel>
                </>
                )
            }
            {
                showHistoryPanel && (
                <>
                    <Separator className={styles.resizeHolder}/>
                    <Panel defaultSize="18%" minSize="18%" maxSize="30%">
                        <ChatHistoryPanel onHistoryItemRemove={handleHistoryItemRemoved} onHistoryCleanup={newChat}/>
                    </Panel>
                </>
                )
            }                   
        </PanelGroup>
        </div>
    )
}

export default project